import { useI18n } from "../../i18n";
import { cn } from "../cn";
import { useOnboarding } from "./useOnboarding";

const go = (hash: string) => {
  window.location.hash = hash;
};

/**
 * OnboardingProfileEntry — Macro #27 entry point on the Profile / account
 * surface. Reuses the SAME onboarding provider/state/backend (no second system,
 * no duplicated state). Behavior is derived from the real onboarding record:
 *   • completed                    → RESTART GUIDE (guidance-only reset)
 *   • engaged (persona/step/dismissed) & not completed → RESUME GUIDE
 *   • pristine (no engagement yet)  → START GUIDE
 * Every variant links to the single control center (#onboarding).
 */
export function OnboardingProfileEntry({ className }: { className?: string }) {
  const { t } = useI18n();
  const onb = useOnboarding();

  if (onb.status !== "ready" || !onb.state) return null;
  const { completed, dismissed, persona, current_step } = onb.state;

  const variant: "start" | "resume" | "restart" = completed
    ? "restart"
    : dismissed || persona !== null || current_step > 1
      ? "resume"
      : "start";

  const ctaLabel =
    variant === "restart"
      ? t("onboarding.entry.restart")
      : variant === "resume"
        ? t("onboarding.entry.resume")
        : t("onboarding.entry.start");

  const onClick = () => {
    if (variant === "restart") {
      void onb.restart();
      go("#onboarding");
      return;
    }
    if (variant === "resume" && dismissed) void onb.patch({ dismissed: false });
    go("#onboarding");
  };

  return (
    <div
      data-testid="onboarding-profile-entry"
      data-variant={variant}
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3.5",
        className,
      )}
    >
      <div className="min-w-0">
        <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-teal-300/80">
          {t("onboarding.profileEntry.eyebrow")}
        </div>
        <p className="mt-1 max-w-prose text-[12.5px] leading-[1.5] text-white/60">{t("onboarding.entry.body")}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        {variant === "restart" && (
          <span data-testid="onboarding-profile-completed" className="font-mono text-[10px] tracking-[0.06em] text-teal-300">
            {t("onboarding.entry.completed")}
          </span>
        )}
        <button
          data-testid={`onboarding-profile-${variant}`}
          onClick={onClick}
          disabled={onb.mutating}
          className="inline-flex items-center rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 no-underline transition-colors hover:bg-teal-400/20 disabled:opacity-50"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
